import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import PrivacyHero from './components/PrivacyHero';
import TableOfContents from './components/TableOfContents';
import DataCollectionSection from './components/DataCollectionSection';
import PurposeSection from './components/PurposeSection';
import SecuritySection from './components/SecuritySection';
import ThirdPartySection from './components/ThirdPartySection';
import UserRightsSection from './components/UserRightsSection';
import CookiesRetentionSection from './components/CookiesRetentionSection';
import InternationalChildrenSection from './components/InternationalChildrenSection';
import PrivacyContactSection from './components/PrivacyContactSection';
import Breadcrumb from '../../components/Breadcrumb';
import PrivacyRelatedPages from './components/PrivacyRelatedPages';

const PrivacyPolicyPage = () => {
  return (
    <>
      <Helmet>
        <title>Personvern & GDPR — NordPass AS</title>
        <meta name="description" content="NordPass sin personvernerklæring. Les om hvordan vi samler inn, bruker og beskytter dine personopplysninger i henhold til GDPR." />
        <link
          href="https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=Lora:ital,wght@0,400;0,700;1,400;1,700&family=Inter:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500;700&display=swap"
          rel="stylesheet"
        />
      </Helmet>
      <div style={{ background: '#050A12', minHeight: '100vh', overflowX: 'hidden' }}>
        {/* Sticky nav */}
        <nav
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 100,
            background: 'rgba(5,10,18,0.92)',
            backdropFilter: 'blur(16px)',
            borderBottom: '1px solid rgba(255,255,255,0.06)'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              padding: '0 5%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              height: '72px'
            }}
          >
            <Link
              to="/nord-pass-landing-page"
              style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}
            >
              <div
                style={{
                  width: '32px',
                  height: '32px',
                  background: '#3B6FD4',
                  clipPath: 'polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 8px 100%, 0 calc(100% - 8px))'
                }}
              />
              <span
                style={{
                  fontFamily: 'Syne, sans-serif',
                  fontSize: '1.125rem',
                  fontWeight: 800,
                  color: '#EEF0F2',
                  letterSpacing: '-0.03em'
                }}
              >
                NordPass
              </span>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: '32px' }}>
              {[
                { label: 'Hjem', href: '/nord-pass-landing-page' },
                { label: 'Tjenester', href: '/tjenester-services' },
                { label: 'Om Oss', href: '/om-oss-about-us' },
                { label: 'Kontakt', href: '/nord-pass-contact-page' },
              ]?.map((link, i) => (
                <Link
                  key={i}
                  to={link?.href}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.875rem',
                    color: 'rgba(238,240,242,0.65)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#EEF0F2'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(238,240,242,0.65)'; }}
                >
                  {link?.label}
                </Link>
              ))}
            </div>

            <Link
              to="/login"
              style={{
                fontFamily: 'Syne, sans-serif',
                fontSize: '0.8125rem',
                fontWeight: 700,
                color: '#EEF0F2',
                textDecoration: 'none',
                padding: '10px 20px',
                background: '#3B6FD4',
                letterSpacing: '0.06em',
                textTransform: 'uppercase'
              }}
            >
              Logg inn
            </Link>
          </div>
        </nav>

        {/* Breadcrumb */}
        <Breadcrumb
          crumbs={[
            { label: 'Hjem', href: '/nord-pass-landing-page' },
            { label: 'Juridisk', href: '/privacy-policy-personvern' },
            { label: 'Personvernerklæring' }
          ]}
        />

        {/* Hero */}
        <PrivacyHero />

        {/* Main layout */}
        <div
          style={{
            maxWidth: '1400px',
            margin: '0 auto',
            padding: '80px 5%',
            display: 'grid',
            gridTemplateColumns: '280px 1fr',
            gap: '60px',
            alignItems: 'start'
          }}
        >
          {/* Sticky sidebar TOC */}
          <aside style={{ position: 'sticky', top: '100px' }}>
            <TableOfContents />

            {/* GDPR badge */}
            <div
              style={{
                marginTop: '24px',
                background: 'rgba(16,185,129,0.06)',
                border: '1px solid rgba(16,185,129,0.2)',
                padding: '20px',
                textAlign: 'center'
              }}
            >
              <div
                style={{
                  fontFamily: 'Impact, sans-serif',
                  fontSize: '2rem',
                  color: '#10B981',
                  letterSpacing: '0.05em',
                  marginBottom: '6px'
                }}
              >
                GDPR
              </div>
              <div
                style={{
                  fontFamily: 'JetBrains Mono, monospace',
                  fontSize: '0.625rem',
                  color: 'rgba(16,185,129,0.7)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase'
                }}
              >
                SAMSVARENDE · EU 2016/679
              </div>
            </div>

            {/* Terms cross-link */}
            <div style={{ marginTop: '16px' }}>
              <Link
                to="/terms-conditions-vilk-r-betingelser"
                style={{
                  display: 'block',
                  background: 'rgba(249,115,22,0.06)',
                  border: '1px solid rgba(249,115,22,0.2)',
                  padding: '16px',
                  textDecoration: 'none',
                  textAlign: 'center',
                  transition: 'all 150ms ease'
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.12)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.06)'; }}
              >
                <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: '0.625rem', color: '#F97316', letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '4px' }}>Se også</div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.8125rem', fontWeight: 700, color: '#EEF0F2' }}>Vilkår & Betingelser →</div>
              </Link>
            </div>
          </aside>

          {/* Main content */}
          <main>
            <DataCollectionSection />
            <PurposeSection />
            <SecuritySection />
            <ThirdPartySection />
            <UserRightsSection />
            <CookiesRetentionSection />
            <InternationalChildrenSection />
            <PrivacyContactSection />

            {/* Related Pages */}
            <PrivacyRelatedPages />
          </main>
        </div>

        {/* Footer strip */}
        <div
          style={{
            borderTop: '1px solid rgba(255,255,255,0.06)',
            background: '#030710',
            padding: '32px 5%'
          }}
        >
          <div
            style={{
              maxWidth: '1400px',
              margin: '0 auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexWrap: 'wrap',
              gap: '16px'
            }}
          >
            <div
              style={{
                fontFamily: 'JetBrains Mono, monospace',
                fontSize: '0.6875rem',
                color: 'rgba(138,149,163,0.4)',
                letterSpacing: '0.06em'
              }}
            >
              © 2026 NordPass AS — Personvernerklæring v2.1 — Sist oppdatert Februar 2026
            </div>
            <div style={{ display: 'flex', gap: '24px' }}>
              {[
                { label: 'Hjem', path: '/nord-pass-landing-page' },
                { label: 'Vilkår', path: '/terms-conditions-vilk-r-betingelser' },
                { label: 'Kontakt', path: '/nord-pass-contact-page' },
              ]?.map((item, i) => (
                <Link
                  key={i}
                  to={item?.path}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontSize: '0.75rem',
                    color: 'rgba(138,149,163,0.4)',
                    textDecoration: 'none',
                    transition: 'color 150ms ease'
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = '#8A95A3'; }}
                  onMouseLeave={e => { e.currentTarget.style.color = 'rgba(138,149,163,0.4)'; }}
                >
                  {item?.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;
